import { FC, useEffect, useRef, useState } from 'react';

import { Loader2, PlugZap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { FullPageErrorState } from '@/modules/common/components/organisms/full-page-error-state';
import { AuthenticatedRoutes } from '@/router/routes';
import { logger } from '@/utils/logger/logger';

import { createMcpLoginHandoff } from '../api/connect-ai.api';
import { consumeConnectAiNonce } from '../utils/connect-ai-nonce';

const TAG = 'ConnectAiPage:';

/**
 * Opened by the Opsium MCP authorization server while an AI app (Claude, ChatGPT, Copilot) is being connected.
 * The user is signed in by the router as for any other page; this page confirms that to the backend and sends
 * the browser back to it, where the user approves the app on the consent screen.
 */
export const ConnectAiPage: FC = () => {
  const { t } = useTranslation('authenticated.connectAi');
  const [hasFailed, setHasFailed] = useState(false);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    // Ensure the handoff is requested only once, even if the component re-renders
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;

    // The URL has it when the user was already signed in; otherwise it was remembered before the login redirect
    const rememberedNonce = consumeConnectAiNonce();
    const nonce = new URLSearchParams(window.location.search).get('nonce') ?? rememberedNonce;
    if (!nonce) {
      logger.error(TAG, 'Opened without a nonce');
      setHasFailed(true);
      return;
    }

    createMcpLoginHandoff(nonce)
      .then(redirectUrl => window.location.assign(redirectUrl))
      .catch(error => {
        logger.error(TAG, 'Failed to create the login handoff', error);
        setHasFailed(true);
      });
  }, []);

  if (hasFailed) {
    return (
      <FullPageErrorState
        icon={PlugZap}
        title={t('error.title')}
        description={t('error.description')}
        actions={
          <Button variant="default" asChild>
            <a href={AuthenticatedRoutes.Planner}>{t('error.goToOpsium')}</a>
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3">
      <Loader2 className="h-4 w-4 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground">{t('connecting')}</p>
    </div>
  );
};
